import {FC, useState} from 'react';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import {Box, Button, CircularProgress, Typography} from '@mui/material';
import {useIntl} from 'react-intl';
import {useBusinessCurrency} from 'src/app/hooks';
import dayjs from 'dayjs';
import {useScreenTier} from 'src/hooks/useScreenTier';
import theme from 'src/theme';
import {ConsumptionChartResponse} from 'src/types';
import {timeSeriesSx} from './TimeSeriesChart.sx';

export type Granularity = 'HOURLY' | 'DAILY' | 'MONTHLY';
type MetricKey = 'consumption' | 'cost' | 'sessions';

interface TimeSeriesChartProps {
    data?: ConsumptionChartResponse;
    loading: boolean;
    granularity: Granularity;
    onGranularityChange: (g: Granularity) => void;
}

const formatTimestamp = (ts: number, gran: Granularity) => {
    const d = dayjs(ts);
    if (gran === 'HOURLY') return d.format('HH:mm');
    if (gran === 'MONTHLY') return d.format('MMM YYYY');
    return d.format('DD MMM');
};

interface ChartPoint {
    name: string;
    consumption: number;
    cost: number;
    sessions: number;
    anomaly: boolean;
}

interface TooltipPayloadItem {
    payload: ChartPoint;
}

interface AxisTickProps {
    x?: number;
    y?: number;
    payload?: {value: string};
}

/**
 * The design's four tones, newest first: the most recent column is the primary blue, and the ones
 * behind it step back through lighter shades of the same colour.
 */
const BAR_TONES: string[] = [
    theme.palette.primary.main,
    theme.palette.primaryColor.A300 as string,
    theme.palette.primaryColor[100] as string,
    theme.palette.primaryColor.A100 as string,
];

// The newest column on its own, then thirds of everything behind it, so a week of days and a year of
// months both read the same way round.
const toneFor = (index: number, total: number): string => {
    const fromEnd = total - 1 - index;
    if (fromEnd <= 0) return BAR_TONES[0];
    const step = Math.max(1, Math.ceil((total - 1) / 3));
    return BAR_TONES[Math.min(BAR_TONES.length - 1, Math.ceil(fromEnd / step))];
};

// Four labels rather than twenty rotated ones: the two ends and two marks between them. Every point
// is still drawn — this is only which of them are named.
const pickTicks = (points: ChartPoint[]): string[] => {
    if (points.length === 0) return [];
    const last = points.length - 1;
    const positions = [0, Math.round(last / 3), Math.round((last * 2) / 3), last];
    return Array.from(new Set(positions)).map((i) => points[i].name);
};

const CustomTooltip: FC<{
    active?: boolean;
    payload?: TooltipPayloadItem[];
    anomalyLabel: string;
    currency: string;
}> = ({active, payload, anomalyLabel, currency}) => {
    if (!active || !payload?.length) return null;
    const point = payload[0].payload;
    return (
        <Box sx={{
            bgcolor: 'white',
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            p: 1.25,
            fontSize: '0.8rem',
        }}>
            <Typography sx={{fontWeight: 600, mb: 0.5, color: 'neutral.700'}}>{point.name}</Typography>
            <Typography sx={{color: 'primary.main'}}>{point.consumption.toFixed(2)} kWh</Typography>
            <Typography sx={{color: 'neutral.600'}}>{point.cost.toFixed(2)} {currency}</Typography>
            <Typography sx={{color: 'neutral.600'}}>{point.sessions} sessions</Typography>
            {point.anomaly && (
                <Typography sx={{color: 'error.main', fontSize: '0.72rem', mt: 0.5, fontWeight: 600}}>
                    ⚠ {anomalyLabel}
                </Typography>
            )}
        </Box>
    );
};

export const TimeSeriesChart: FC<TimeSeriesChartProps> = ({
    data,
    loading,
    granularity,
    onGranularityChange,
}) => {
    const intl = useIntl();
    const fallbackCurrency = useBusinessCurrency();
    // A line is one scale. When the range spans locations priced in different currencies there is no
    // single scale to draw cost on, so the metric is not offered rather than drawn as a sum of them -
    // the server returns null per point in that case, and a null plotted as zero is a flat lie.
    const currencies = data?.costs ?? [];
    const singleCurrency = currencies.length === 1 ? currencies[0].currency : null;
    const costIsPlottable = currencies.length <= 1;
    // Below md the series is drawn as columns; from md up it is the same area chart it has always been.
    const {isCompact} = useScreenTier();
    const [metric, setMetric] = useState<MetricKey>('consumption');

    const metricTabs: Array<{value: MetricKey; labelId: string}> = [
        {value: 'consumption', labelId: 'ecConsumptionOverTime'},
        ...(costIsPlottable ? [{value: 'cost' as MetricKey, labelId: 'ecCostOverTime'}] : []),
        {value: 'sessions', labelId: 'ecSessionsOverTime'},
    ];

    const granularityOptions: Array<{value: Granularity; labelId: string}> = [
        {value: 'HOURLY', labelId: 'ecGranularityHourly'},
        {value: 'DAILY', labelId: 'ecGranularityDaily'},
        {value: 'MONTHLY', labelId: 'ecGranularityMonthly'},
    ];

    const chartData: ChartPoint[] = data?.points?.map((p) => ({
        name: formatTimestamp(p.timestamp, granularity),
        consumption: p.consumption / 1000,
        cost: (p.cost ?? 0) / 10000,
        sessions: p.transactionCount,
        anomaly: p.anomaly,
    })) ?? [];

    const metricConfig: Record<MetricKey, {unit: string; decimals: number; color: string; gradientId: string}> = {
        consumption: {unit: ' kWh', decimals: 1, color: '#007FDC', gradientId: 'gradConsumption'},
        cost: {unit: ` ${singleCurrency ?? fallbackCurrency}`, decimals: 2, color: '#399646', gradientId: 'gradCost'},
        sessions: {unit: '', decimals: 0, color: '#ff7d22', gradientId: 'gradSessions'},
    };

    const currentConfig = metricConfig[metric];

    const phoneTicks = pickTicks(chartData);
    const lastLabel = chartData.length > 0 ? chartData[chartData.length - 1].name : '';

    // The label at the right-hand end is the one the reader is standing on, so it is the only one in
    // the text colour; the marks behind it stay quiet.
    const renderPhoneTick = (props: AxisTickProps) => {
        const {x, y, payload} = props;
        const isLast = payload?.value === lastLabel;
        return (
            <text
                key={`tick-${payload?.value}`}
                x={x}
                y={y}
                dy={12}
                textAnchor="middle"
                fontSize={11}
                fontWeight={isLast ? 600 : 500}
                fill={isLast ? theme.palette.text.primary : theme.palette.neutral[500]}
            >
                {payload?.value}
            </text>
        );
    };

    const tooltip = (
        <Tooltip
            content={
                <CustomTooltip
                    anomalyLabel={intl.formatMessage({id: 'ecAnomaly'})}
                    currency={singleCurrency ?? fallbackCurrency}
                />
            }
            cursor={isCompact ? {fill: theme.palette.neutral[50]} : {stroke: `${currentConfig.color}25`, strokeWidth: 2}}
        />
    );

    return (
        <Box sx={timeSeriesSx.root}>
            <Box sx={timeSeriesSx.tabsBar}>
                <Box sx={timeSeriesSx.tabs}>
                    {metricTabs.map((opt) => (
                        <Typography
                            key={opt.value}
                            sx={metric === opt.value ? timeSeriesSx.tabActive : timeSeriesSx.tab}
                            onClick={() => setMetric(opt.value)}
                        >
                            {intl.formatMessage({id: opt.labelId})}
                        </Typography>
                    ))}
                </Box>
            </Box>

            <Box sx={timeSeriesSx.content}>
                <Box sx={timeSeriesSx.header}>
                    <Box sx={timeSeriesSx.segmentedRoot}>
                        {granularityOptions.map((opt) => (
                            <Button
                                key={opt.value}
                                sx={granularity === opt.value ? timeSeriesSx.segmentButtonActive : timeSeriesSx.segmentButton}
                                onClick={() => onGranularityChange(opt.value)}
                                disableRipple
                            >
                                {intl.formatMessage({id: opt.labelId})}
                            </Button>
                        ))}
                    </Box>
                </Box>

                {loading ? (
                    <Box sx={timeSeriesSx.empty}>
                        <CircularProgress size={28} />
                    </Box>
                ) : chartData.length === 0 ? (
                    <Box sx={timeSeriesSx.empty}>
                        {intl.formatMessage({id: 'ecNoData'})}
                    </Box>
                ) : (
                    <Box sx={timeSeriesSx.chartContainer}>
                        <ResponsiveContainer width="100%" height="100%">
                            {isCompact ? (
                                <BarChart data={chartData} margin={{top: 6, right: 2, left: 2, bottom: 0}}>
                                    <XAxis
                                        dataKey="name"
                                        ticks={phoneTicks}
                                        interval={0}
                                        tickLine={false}
                                        axisLine={false}
                                        tick={renderPhoneTick}
                                        height={26}
                                    />
                                    {/* Hidden rather than absent: the scale is still the data's, but a
                                        80px axis of numbers is a fifth of a phone screen. */}
                                    <YAxis hide />
                                    {tooltip}
                                    <Bar dataKey={metric} radius={[6, 6, 0, 0]}>
                                        {chartData.map((point, index) => (
                                            <Cell
                                                key={`${point.name}-${index}`}
                                                fill={toneFor(index, chartData.length)}
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            ) : (
                                <AreaChart data={chartData} margin={{top: 5, right: 10, left: 0, bottom: 5}}>
                                    <defs>
                                        <linearGradient id={currentConfig.gradientId} x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor={currentConfig.color} stopOpacity={0.35} />
                                            <stop offset="100%" stopColor={currentConfig.color} stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
                                    <XAxis
                                        dataKey="name"
                                        fontSize={11}
                                        tickLine={false}
                                        axisLine={false}
                                        stroke="#9090B0"
                                    />
                                    <YAxis
                                        fontSize={11}
                                        tickLine={false}
                                        axisLine={false}
                                        stroke="#9090B0"
                                        unit={currentConfig.unit}
                                        width={80}
                                    />
                                    {tooltip}
                                    <Area
                                        type="monotone"
                                        dataKey={metric}
                                        stroke={currentConfig.color}
                                        strokeWidth={2}
                                        fill={`url(#${currentConfig.gradientId})`}
                                        dot={(props) => {
                                            const {cx, cy, payload, index} = props;
                                            if (!payload.anomaly) {
                                                return <circle key={`dot-${index}`} cx={cx} cy={cy} r={0} />;
                                            }
                                            return (
                                                <circle
                                                    key={`anomaly-${index}`}
                                                    cx={cx}
                                                    cy={cy}
                                                    r={5}
                                                    fill="#e62930"
                                                    stroke="white"
                                                    strokeWidth={2}
                                                />
                                            );
                                        }}
                                        activeDot={{r: 5, fill: currentConfig.color, stroke: 'white', strokeWidth: 2}}
                                    />
                                </AreaChart>
                            )}
                        </ResponsiveContainer>
                    </Box>
                )}
            </Box>
        </Box>
    );
};
